import { Injectable } from '@angular/core';
import { devLog, devError } from '@core';
import {
  NotificationTemplateType,
  UrgencyLevel,
  NotificationTemplate,
  TemplateContext,
  RenderedNotification,
  IOSPayload,
  AndroidPayload
} from '@core/types/notification-template.types';

/**
 * Nudge Notification Template Service
 *
 * Manages notification templates for check-in reminders with:
 * - Multiple urgency levels (low to critical)
 * - Localization support
 * - Platform-specific formatting (iOS/Android)
 * - Dynamic content interpolation
 *
 * CRITICAL CONSTRAINTS:
 * - Use devLog() instead of console.log
 * - Zero any types - all explicit
 *
 * @see WI-036 Nudge Notification Templates
 */
@Injectable({
  providedIn: 'root'
})
export class NudgeTemplateService {
  private templates = new Map<NotificationTemplateType, NotificationTemplate>();
  private translations = new Map<string, Record<string, string>>();

  constructor() {
    this.initializeTemplates();
  }

  /**
   * Get template definition by type
   */
  getTemplate(type: NotificationTemplateType): NotificationTemplate {
    const template = this.templates.get(type);
    if (!template) {
      throw new Error(`Template not found for type: ${type}`);
    }
    return template;
  }

  /**
   * Render notification with context data
   */
  async renderNotification(
    type: NotificationTemplateType,
    context: TemplateContext,
    locale: string = 'en-US'
  ): Promise<RenderedNotification> {
    const template = this.getTemplate(type);
    const urgency = this.calculateUrgency(type, context.timing);

    // Load translations if not cached
    await this.loadTranslations(locale);

    // Render title and body with localization
    const title = this.renderText(template.title, context, locale);
    const body = this.renderText(template.body, context, locale);

    devLog('[NudgeTemplateService] Rendering notification:', {
      type,
      urgency,
      locale
    });

    return {
      title,
      body,
      data: {
        type,
        checkInId: context.checkIn.id,
        urgency,
        timestamp: new Date().toISOString()
      },
      ios: this.formatForIOS(template, urgency),
      android: this.formatForAndroid(template, urgency)
    };
  }

  /**
   * Calculate urgency based on timing and notification type
   */
  calculateUrgency(
    type: NotificationTemplateType,
    timing: TemplateContext['timing']
  ): UrgencyLevel {
    if (type === NotificationTemplateType.FINAL_WARNING || timing.isLastChance) {
      return UrgencyLevel.CRITICAL;
    }

    if (type === NotificationTemplateType.SECOND_NUDGE || timing.minutesUntilEscalation < 10) {
      return UrgencyLevel.HIGH;
    }

    if (timing.minutesOverdue > 5) {
      return UrgencyLevel.MEDIUM;
    }

    return UrgencyLevel.LOW;
  }

  /**
   * Load translations for a locale
   */
  async loadTranslations(locale: string): Promise<void> {
    if (this.translations.has(locale)) {
      return;
    }

    try {
      const response = await fetch(`/assets/i18n/notifications/${locale}.json`);
      const data = await response.json() as Record<string, string>;
      this.translations.set(locale, data);
      devLog('[NudgeTemplateService] Loaded translations for:', locale);
    } catch (error: unknown) {
      devError('[NudgeTemplateService] Failed to load translations:', locale, error);
      // Continue with fallbacks
    }
  }

  /**
   * Initialize template definitions
   */
  private initializeTemplates(): void {
    // First nudge template
    this.templates.set(NotificationTemplateType.FIRST_NUDGE, {
      id: 'first_nudge',
      type: NotificationTemplateType.FIRST_NUDGE,
      urgencyLevel: UrgencyLevel.MEDIUM,
      title: {
        key: 'nudge.first.title',
        fallback: 'Time to Check In'
      },
      body: {
        key: 'nudge.first.body',
        fallback: 'Your check-in "{{note}}" is due'
      },
      actions: [
        {
          id: 'check-in',
          titleKey: 'action.checkIn',
          titleFallback: 'Check In',
          style: 'primary',
          requiresAuth: false,
          launchApp: true
        },
        {
          id: 'snooze',
          titleKey: 'action.snooze',
          titleFallback: 'Snooze 30 min',
          style: 'default',
          requiresAuth: false,
          launchApp: false
        }
      ],
      sound: { name: 'default', critical: false, volume: 0.7 },
      vibrationPattern: [0, 250, 250, 250],
      badge: { type: 'set', value: 1 },
      color: '#14B8A6',
      icon: 'notification_clock',
      category: 'CHECK_IN_REMINDER'
    });

    // Second nudge template
    this.templates.set(NotificationTemplateType.SECOND_NUDGE, {
      id: 'second_nudge',
      type: NotificationTemplateType.SECOND_NUDGE,
      urgencyLevel: UrgencyLevel.HIGH,
      title: {
        key: 'nudge.second.title',
        fallback: 'Urgent: Check In Required'
      },
      body: {
        key: 'nudge.second.body',
        fallback: '{{contactCount}} contacts will be notified in {{minutesRemaining}} minutes'
      },
      actions: [
        {
          id: 'check-in',
          titleKey: 'action.checkInNow',
          titleFallback: 'Check In Now!',
          style: 'primary',
          requiresAuth: false,
          launchApp: true
        },
        {
          id: 'snooze',
          titleKey: 'action.snooze',
          titleFallback: 'Snooze',
          style: 'default',
          requiresAuth: false,
          launchApp: false
        }
      ],
      sound: { name: 'urgent', critical: false, volume: 0.9 },
      vibrationPattern: [0, 500, 200, 500, 200, 500],
      badge: { type: 'set', value: 1 },
      color: '#F59E0B',
      icon: 'notification_warning',
      category: 'CHECK_IN_URGENT'
    });

    // Final warning template
    this.templates.set(NotificationTemplateType.FINAL_WARNING, {
      id: 'final_warning',
      type: NotificationTemplateType.FINAL_WARNING,
      urgencyLevel: UrgencyLevel.CRITICAL,
      title: {
        key: 'nudge.final.title',
        fallback: 'FINAL WARNING'
      },
      body: {
        key: 'nudge.final.body',
        fallback: 'Alerting {{contactCount}} contacts in 2 minutes!'
      },
      actions: [
        {
          id: 'check-in',
          titleKey: 'action.checkInUrgent',
          titleFallback: 'CHECK IN NOW',
          style: 'primary',
          requiresAuth: false,
          launchApp: true
        }
      ],
      sound: { name: 'critical', critical: true, volume: 1.0 },
      vibrationPattern: [0, 1000, 500, 1000, 500, 1000],
      badge: { type: 'set', value: 1 },
      color: '#EF4444',
      icon: 'notification_critical',
      category: 'CHECK_IN_CRITICAL'
    });
  }

  /**
   * Render localized text with variable interpolation
   */
  private renderText(
    text: { key: string; fallback: string },
    context: TemplateContext,
    locale: string
  ): string {
    // Get localized string or fallback
    const translations = this.translations.get(locale);
    let rendered = translations?.[text.key] ?? text.fallback;

    // Interpolate variables
    rendered = this.interpolate(rendered, {
      note: context.checkIn.note || 'your scheduled check-in',
      contactCount: String(context.checkIn.contactCount),
      minutesRemaining: String(context.timing.minutesUntilEscalation),
      minutesOverdue: String(context.timing.minutesOverdue),
      userName: context.user.name
    });

    return rendered;
  }

  /**
   * Replace template variables with values
   */
  private interpolate(
    template: string,
    values: Record<string, string>
  ): string {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
      return values[key] ?? match;
    });
  }

  /**
   * Format notification for iOS APNS
   */
  formatForIOS(
    template: NotificationTemplate,
    urgency: UrgencyLevel
  ): IOSPayload {
    const sound = template.sound.critical
      ? { critical: 1, name: `${template.sound.name}.wav`, volume: template.sound.volume }
      : template.sound.name;

    const interruptionLevel = urgency === UrgencyLevel.CRITICAL
      ? 'critical'
      : urgency === UrgencyLevel.HIGH
        ? 'time-sensitive'
        : 'active';

    return {
      aps: {
        badge: template.badge.value,
        sound,
        category: template.category,
        threadId: `check-in-${template.id}`,
        interruptionLevel
      }
    };
  }

  /**
   * Format notification for Android FCM
   */
  formatForAndroid(
    template: NotificationTemplate,
    urgency: UrgencyLevel
  ): AndroidPayload {
    const channelId = this.getAndroidChannel(urgency);
    const priority = urgency === UrgencyLevel.CRITICAL || urgency === UrgencyLevel.HIGH
      ? 'high'
      : 'normal';

    return {
      priority,
      notification: {
        channelId,
        icon: template.icon,
        color: template.color,
        sound: template.sound.name,
        vibrate: template.vibrationPattern,
        notificationPriority: this.getAndroidPriority(urgency)
      }
    };
  }

  /**
   * Get Android notification channel by urgency
   */
  private getAndroidChannel(urgency: UrgencyLevel): string {
    switch (urgency) {
      case UrgencyLevel.CRITICAL:
        return 'critical-alerts';
      case UrgencyLevel.HIGH:
        return 'urgent-reminders';
      case UrgencyLevel.MEDIUM:
        return 'check-in-reminders';
      default:
        return 'general';
    }
  }

  /**
   * Get Android notification priority
   */
  private getAndroidPriority(urgency: UrgencyLevel): number {
    switch (urgency) {
      case UrgencyLevel.CRITICAL:
        return 2; // PRIORITY_MAX
      case UrgencyLevel.HIGH:
        return 1; // PRIORITY_HIGH
      case UrgencyLevel.MEDIUM:
        return 0; // PRIORITY_DEFAULT
      default:
        return -1; // PRIORITY_LOW
    }
  }
}
